import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { type QueryFnBaseQuery } from './ContentItemEditor.types';

// Appends a file part, reading the bytes rather than passing the picked File straight through.
// Chromium re-checks a File's size and mtime when it reads the request body, and a cloud provider
// that hydrates a placeholder file on first read fails that check with ERR_UPLOAD_FILE_CHANGED. The
// Blob carries no name, so the filename goes in explicitly. Same helper as
// ProjectSubmission/uploadProjectSubmissionApi.
async function appendFile(formData: FormData, key: string, file: File) {
    const blob = new Blob([await file.arrayBuffer()], { type: file.type });
    formData.append(key, blob, file.name);
}

async function buildUploadFormData(key: string, files: File[]): Promise<FormData | FetchBaseQueryError> {
    const formData = new FormData();
    try {
        for (const file of files) {
            await appendFile(formData, key, file);
        }
        return formData;
    } catch (e) {
        const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
        ErrorLogService?.notify('Could not read a content item editor upload', e, { file_count: files.length });
        return { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : String(e) };
    }
}

export async function uploadFiles<Response>(
    { url, key, files }: { url: string; key: string; files: File[] },
    fetchWithBaseQuery: QueryFnBaseQuery,
): Promise<{ data: Response; error?: undefined } | { error: FetchBaseQueryError; data?: undefined }> {
    const formData = await buildUploadFormData(key, files);
    if (!(formData instanceof FormData)) return { error: formData };

    const response = (await fetchWithBaseQuery({ url, method: 'POST', body: formData })) as {
        data?: Response;
        error?: FetchBaseQueryError;
    };

    if (response.error) return { error: response.error };
    return { data: response.data as Response };
}
